/**
 * Platform Admin → AI 管理 → Skill 库
 * (VISTOERA_PLATFORM_SKILL_LIBRARY_AND_REGISTRY_V1)
 */
import {
  DownOutlined,
  ExperimentOutlined,
  FileTextOutlined,
  ImportOutlined,
  PlusOutlined,
  RocketOutlined,
  SearchOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons'
import { Button, Dropdown, Input, Modal, Select, Table, Tag, Typography, message } from 'antd'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { platformCreateSkill, platformImportSkillPackage, platformListSkills } from '../api'
import type { SkillCatalogPayload, SkillLibraryListResponse, SkillListRow } from '../types'
import { SummaryCard, formatDateTime } from './adminShared'
import { platformColors } from './theme'
import './skill-library.css'

type ViewTab = 'all' | 'by_stage' | 'by_domain'

const STATUS_TONE: Record<string, { color: string; label: string }> = {
  PRODUCTION: { color: '#22c55e', label: '生产中' },
  EXPERIMENTING: { color: '#f59e0b', label: '实验中' },
  TESTING: { color: '#f59e0b', label: '测试中' },
  DRAFT: { color: '#94a3b8', label: '草稿' },
  DISABLED: { color: '#64748b', label: '已停用' },
  ARCHIVED: { color: '#64748b', label: '已归档' },
}

function StatusDot({ status }: { status: string }) {
  const tone = STATUS_TONE[status] ?? STATUS_TONE.DRAFT
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: tone.color }} />
      <span>{tone.label}</span>
    </span>
  )
}

function TrafficBar({ label }: { label: string }) {
  const parts = label.split('/').map((p) => p.trim())
  if (label === '0%' || !label) {
    return <span style={{ color: platformColors.mutedText }}>0%</span>
  }
  if (parts.length === 1) {
    const pct = Number.parseInt(parts[0], 10) || 0
    return (
      <div className="skill-traffic">
        <div className="skill-traffic-track">
          <div className="skill-traffic-fill skill-traffic-fill--prod" style={{ width: `${pct}%` }} />
        </div>
        <span>{label}</span>
      </div>
    )
  }
  const a = Number.parseInt(parts[0], 10) || 0
  const b = Number.parseInt(parts[1], 10) || 0
  return (
    <div className="skill-traffic">
      <div className="skill-traffic-track">
        <div className="skill-traffic-fill skill-traffic-fill--prod" style={{ width: `${a}%` }} />
        <div className="skill-traffic-fill skill-traffic-fill--exp" style={{ width: `${b}%` }} />
      </div>
      <span>{label}</span>
    </div>
  )
}

export default function SkillLibraryPage() {
  const navigate = useNavigate()
  const [data, setData] = useState<SkillLibraryListResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState<ViewTab>('all')
  const [domain, setDomain] = useState<string | undefined>()
  const [stageKey, setStageKey] = useState<string | undefined>()
  const [status, setStatus] = useState<string | undefined>()
  const [q, setQ] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [form, setForm] = useState({
    skill_key: '',
    display_name: '',
    domain: 'CONTENT_PRODUCTION',
    stage_key: '01_IDEATION',
    description: '',
    compatible_capabilities: '',
  })
  // VISTOERA_SKILL_PACKAGE_REGISTRY_..._P0_V1 §9 — external Skill Package
  // import foundation. Always lands UNTRUSTED + DRAFT server-side; never
  // auto-promoted to PRODUCTION regardless of what is selected here.
  const [importOpen, setImportOpen] = useState(false)
  const [importing, setImporting] = useState(false)
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importSourceKind, setImportSourceKind] = useState<'VISTOERA_PACKAGE' | 'EXTERNAL'>('EXTERNAL')
  const importInputRef = useRef<HTMLInputElement | null>(null)

  const catalog: SkillCatalogPayload | undefined = data?.catalog

  const load = useCallback(() => {
    setLoading(true)
    platformListSkills({
      domain,
      stage_key: stageKey,
      status,
      q: q.trim() || undefined,
      view,
    })
      .then((res) => setData(res.data))
      .catch((err) => message.error(err?.response?.data?.detail?.message || '加载 Skill 库失败'))
      .finally(() => setLoading(false))
  }, [domain, stageKey, status, q, view])

  useEffect(() => {
    load()
  }, [load])

  const metrics = data?.metrics
  const columns = useMemo(
    () => [
      {
        title: 'Skill',
        key: 'skill',
        render: (_: unknown, row: SkillListRow) => (
          <Link to={`/platform/skills/${row.id}`} className="skill-name-link">
            <div className="skill-name">{row.display_name}</div>
            <div className="skill-desc">{row.description || row.skill_key}</div>
          </Link>
        ),
      },
      {
        title: '阶段',
        dataIndex: 'stage_label',
        key: 'stage',
        render: (v: string | null) => v || '—',
      },
      {
        title: '域',
        dataIndex: 'domain_label',
        key: 'domain',
        render: (v: string | null, row: SkillListRow) => v || row.domain,
      },
      {
        title: '当前版本',
        key: 'version',
        render: (_: unknown, row: SkillListRow) => (
          <span>
            {row.current_version || '—'}
            {row.is_latest_production && row.current_version ? (
              <Tag color="success" style={{ marginLeft: 6, fontSize: 11 }}>
                最新版
              </Tag>
            ) : null}
          </span>
        ),
      },
      {
        title: '状态',
        dataIndex: 'status',
        key: 'status',
        render: (s: string) => <StatusDot status={s} />,
      },
      {
        title: '生产流量',
        dataIndex: 'production_traffic',
        key: 'traffic',
        render: (v: string) => <TrafficBar label={v || '0%'} />,
      },
      {
        title: '总执行次数',
        dataIndex: 'total_executions',
        key: 'exec',
        render: (n: number) => (n ?? 0).toLocaleString('zh-CN'),
      },
      {
        title: '成功率',
        dataIndex: 'success_rate',
        key: 'rate',
        render: (n: number | null) => (n == null ? '—' : `${n}%`),
      },
      {
        title: '更新时间',
        dataIndex: 'updated_at',
        key: 'updated',
        render: (v: string | null) => formatDateTime(v),
      },
    ],
    [],
  )

  const onCreate = async () => {
    if (!form.skill_key.trim() || !form.display_name.trim()) {
      message.warning('请填写 skill_key 与显示名称')
      return
    }
    setCreating(true)
    try {
      const compatible_capabilities = form.compatible_capabilities
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean)
      const res = await platformCreateSkill({
        skill_key: form.skill_key.trim(),
        display_name: form.display_name.trim(),
        domain: form.domain,
        stage_key: form.stage_key || undefined,
        description: form.description.trim() || undefined,
        compatible_capabilities: compatible_capabilities.length ? compatible_capabilities : undefined,
      })
      message.success('已创建 Skill（v1.0.0 DRAFT）')
      setCreateOpen(false)
      navigate(`/platform/skills/${res.data.id}`)
    } catch (err: any) {
      message.error(err?.response?.data?.detail?.message || '创建失败')
    } finally {
      setCreating(false)
    }
  }

  const onImport = async () => {
    if (!importFile) {
      message.warning('请选择 Skill Package (.zip)')
      return
    }
    setImporting(true)
    try {
      const res = await platformImportSkillPackage(importFile, importSourceKind)
      message.success('导入成功：已生成 DRAFT 版本（UNTRUSTED，需管理员审核后才可发布为 Production）')
      setImportOpen(false)
      setImportFile(null)
      navigate(`/platform/skills/${res.data.id}`)
    } catch (err: any) {
      message.error(err?.response?.data?.detail?.message || '导入失败')
    } finally {
      setImporting(false)
    }
  }

  return (
    <div className="skill-library-page">
      <div className="skill-library-header">
        <div>
          <div className="skill-breadcrumb">平台管理 / Skill 库</div>
          <h1 className="skill-title">Skill 库</h1>
          <p className="skill-subtitle">
            管理 AI Skill 的定义、版本、部署、实验与效果数据，驱动内容生产全流程智能优化。
          </p>
        </div>
        <Dropdown
          menu={{
            items: [
              { key: 'blank', icon: <PlusOutlined />, label: '从空白创建' },
              { key: 'import', icon: <ImportOutlined />, label: '导入 Skill Package' },
            ],
            onClick: ({ key }) => {
              if (key === 'blank') setCreateOpen(true)
              else if (key === 'import') setImportOpen(true)
            },
          }}
        >
          <Button type="primary" icon={<PlusOutlined />}>
            创建 Skill <DownOutlined />
          </Button>
        </Dropdown>
      </div>

      <div className="skill-metrics-grid">
        <SummaryCard
          title="Skill 总数"
          value={metrics?.skill_total ?? 0}
          hint="全部状态"
          iconBg="#1e3a5f"
          icon={<ThunderboltOutlined />}
          loading={loading && !data}
        />
        <SummaryCard
          title="生产中 Skill"
          value={metrics?.production_count ?? 0}
          hint="当前在用"
          iconBg="#14532d"
          icon={<RocketOutlined />}
          loading={loading && !data}
        />
        <SummaryCard
          title="实验中"
          value={metrics?.experimenting_count ?? 0}
          hint="A/B 与金丝雀"
          iconBg="#78350f"
          icon={<ExperimentOutlined />}
          loading={loading && !data}
        />
        <SummaryCard
          title="草稿"
          value={metrics?.draft_count ?? 0}
          hint="开发 / 待发布"
          iconBg="#334155"
          icon={<FileTextOutlined />}
          loading={loading && !data}
        />
        <SummaryCard
          title="总执行次数"
          value={(metrics?.total_executions ?? 0).toLocaleString('zh-CN')}
          hint="真实累计（无数据为 0）"
          iconBg="#1e3a5f"
          icon={<ThunderboltOutlined />}
          loading={loading && !data}
        />
        <SummaryCard
          title="平均成功率"
          value={metrics?.average_success_rate == null ? '—' : `${metrics.average_success_rate}%`}
          hint="无执行时显示 —"
          iconBg="#14532d"
          icon={<RocketOutlined />}
          loading={loading && !data}
        />
      </div>

      <div className="skill-toolbar">
        <div className="skill-tabs">
          {(
            [
              ['all', '全部 Skill'],
              ['by_stage', '按阶段查看'],
              ['by_domain', '按域查看'],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`skill-tab${view === key ? ' skill-tab--active' : ''}`}
              onClick={() => setView(key)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="skill-filters">
          <Select
            allowClear
            placeholder="全部域"
            style={{ width: 140 }}
            value={domain}
            onChange={setDomain}
            options={(catalog?.domains || []).map((d) => ({ value: d.key, label: d.label }))}
          />
          <Select
            allowClear
            placeholder="全部阶段"
            style={{ width: 180 }}
            value={stageKey}
            onChange={setStageKey}
            options={(catalog?.stages || []).map((s) => ({ value: s.key, label: s.label }))}
          />
          <Select
            allowClear
            placeholder="全部状态"
            style={{ width: 130 }}
            value={status}
            onChange={setStatus}
            options={(catalog?.lifecycle_statuses || []).map((s) => ({ value: s.key, label: s.label }))}
          />
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="搜索 Skill 名称或描述"
            style={{ width: 240 }}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onPressEnter={load}
          />
        </div>
      </div>

      <div className="skill-table-wrap">
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={data?.items || []}
          pagination={{
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            defaultPageSize: 10,
          }}
          onRow={(row) => ({
            onClick: () => navigate(`/platform/skills/${row.id}`),
            style: { cursor: 'pointer' },
          })}
        />
      </div>

      <Modal
        title="创建 Skill"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={onCreate}
        confirmLoading={creating}
        okText="创建"
        destroyOnClose
      >
        <div className="skill-create-form">
          <label>
            显示名称
            <Input
              value={form.display_name}
              onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
              placeholder="内容选题导演"
            />
          </label>
          <label>
            skill_key（稳定标识）
            <Input
              value={form.skill_key}
              onChange={(e) => setForm((f) => ({ ...f, skill_key: e.target.value }))}
              placeholder="content_ideation_director"
            />
          </label>
          <label>
            域
            <Select
              style={{ width: '100%' }}
              value={form.domain}
              onChange={(v) => setForm((f) => ({ ...f, domain: v }))}
              options={(catalog?.domains || []).map((d) => ({ value: d.key, label: d.label }))}
            />
          </label>
          <label>
            阶段
            <Select
              allowClear
              style={{ width: '100%' }}
              value={form.stage_key}
              onChange={(v) => setForm((f) => ({ ...f, stage_key: v || '' }))}
              options={(catalog?.stages || []).map((s) => ({ value: s.key, label: s.label }))}
            />
          </label>
          <label>
            描述
            <Input.TextArea
              rows={3}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </label>
          <label>
            兼容 Capability（逗号分隔，决定可绑定到哪些功能节点）
            <Input
              value={form.compatible_capabilities}
              onChange={(e) => setForm((f) => ({ ...f, compatible_capabilities: e.target.value }))}
              placeholder="text.generation, video.prompt.enhance"
            />
          </label>
        </div>
      </Modal>

      <Modal
        title="导入 Skill Package"
        open={importOpen}
        onCancel={() => {
          setImportOpen(false)
          setImportFile(null)
        }}
        onOk={() => void onImport()}
        confirmLoading={importing}
        okText="导入"
        okButtonProps={{ disabled: !importFile }}
        destroyOnClose
      >
        <div className="skill-create-form">
          <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
            上传符合 <code>vistoera.skill/v1</code> 规范的 <code>.zip</code> 包（skill.yaml + SKILL.md +
            input/output schema）。导入结果始终为 <b>UNTRUSTED / DRAFT</b>，绝不会自动发布为 Production；
            管理员审核通过后才能提升为 Production。禁止包含 .py/.js/.sh/.exe
            等可执行文件，包内不得包含 Provider API Key / Secret。
          </Typography.Paragraph>
          <label>
            来源类型
            <Select
              style={{ width: '100%' }}
              value={importSourceKind}
              onChange={setImportSourceKind}
              options={[
                { value: 'VISTOERA_PACKAGE', label: 'Vistoera 官方 Package' },
                { value: 'EXTERNAL', label: '外部 / 第三方 Skill Package' },
              ]}
            />
          </label>
          <label>
            Skill Package (.zip)
            <div style={{ marginTop: 6 }}>
              <input
                ref={importInputRef}
                type="file"
                accept=".zip"
                style={{ display: 'none' }}
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
              />
              <Button onClick={() => importInputRef.current?.click()}>选择 .zip 文件</Button>
              {importFile ? (
                <Typography.Text style={{ marginLeft: 10 }}>{importFile.name}</Typography.Text>
              ) : null}
            </div>
          </label>
        </div>
      </Modal>
    </div>
  )
}
